/**
 * Stage / Briefing mission content resolution.
 *
 * Fallback rules (always produce a renderable mission — never a "missing" state):
 * 1. no prompt → mission title rendered huge
 * 2. no structured script steps → existing notes blob shown in the drawer as-is
 * 3. no say-lines → drawer is just do-steps
 */

export type StageMissionType =
  | 'welcome'
  | 'warm-up'
  | 'activity'
  | 'capture'
  | 'reveal'
  | 'reflect'
  | 'other';

export type MissionPriority = 'core' | 'flex';

export type ScriptStep = {
  kind: 'do' | 'say';
  text: string;
};

export type WorksheetSpec = {
  title: string;
  /** Simple printable HTML body (browser print) */
  printHtml: string;
};

export type UploadedWorksheet = {
  id: string;
  title: string;
  url: string;
  ageGroup: 'k-2' | '3-5' | '6-8' | 'k-8';
};

export type ResolvedStageMission = {
  id: string;
  title: string;
  missionType: StageMissionType;
  priority: MissionPriority;
  plannedMinutes: number | null;
  /** Always set (fallback: title) */
  prompt: string;
  /** Optional secondary kid-facing line under the prompt */
  subtitle?: string;
  slideImage?: string;
  worksheet?: WorksheetSpec;
  /** Structured do/say steps when authored */
  script: ScriptStep[];
  /** Unstructured teacher blob when script is empty (fallback rule 2) */
  notesBlob?: string;
  attachments: Array<{ id: string; title: string; url: string; kind?: string }>;
  interactive?: { kind: 'game'; link?: string; gameId?: string };
};

export type LevelBriefing = {
  summary: string;
  teacherNotes?: string;
  openingSay?: string;
  videoUrl?: string;
  slideDeckUrl?: string;
  iCanStatement?: string;
  additionalNotes?: string;
  resources: Array<{ id: string; title: string; url: string; kind?: string; description?: string }>;
  uploadedWorksheets: UploadedWorksheet[];
  worksheet?: WorksheetSpec;
  missions: Array<{ id: string; title: string; plannedMinutes: number | null; priority: MissionPriority }>;
};

function asString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function htmlToText(value: string): string {
  return value
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>|<\/div>|<\/li>|<\/h[1-6]>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .split('\n')
    .map(part => part.trim())
    .filter(Boolean)
    .join('\n');
}

export function parseDurationMinutes(duration?: string): number | null {
  if (!duration) return null;
  const m = duration.match(/(\d+)\s*(?:–|-)?\s*\d*\s*min/i) || duration.match(/(\d+)/);
  if (!m) return null;
  const n = Number(m[1]);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function stripMissionPrefix(title: string): string {
  return title.replace(/^Class\s+\d+\s*:\s*/i, '').trim() || title;
}

function flattenInstructions(instructions: unknown): string {
  if (!Array.isArray(instructions)) return '';
  const lines: string[] = [];
  for (const item of instructions) {
    if (typeof item === 'string') {
      lines.push(item);
      continue;
    }
    if (item && typeof item === 'object') {
      if ((item as any).type === 'section') {
        lines.push((item as any).title || '');
        ((item as any).items || []).forEach((s: string) => lines.push(`  ${s}`));
      } else if ((item as any).text) {
        lines.push((item as any).text);
        ((item as any).details || []).forEach((d: string) => lines.push(`  • ${d}`));
      }
    }
  }
  return lines.filter(Boolean).join('\n');
}

function inferMissionType(id: string, text: string, activity: any): StageMissionType {
  const explicit = asString(activity?.missionType || activity?.stageType).toLowerCase();
  if (
    explicit === 'welcome' ||
    explicit === 'warm-up' ||
    explicit === 'activity' ||
    explicit === 'capture' ||
    explicit === 'reveal' ||
    explicit === 'reflect' ||
    explicit === 'other'
  ) {
    return explicit as StageMissionType;
  }
  const hay = `${id} ${text}`.toLowerCase();
  if (hay.includes('capture') || hay.includes('photograph')) return 'capture';
  if (hay.includes('reveal')) return 'reveal';
  if (hay.includes('reflect')) return 'reflect';
  if (hay.includes('welcome') || hay.includes('overview') || hay.includes('review')) return 'welcome';
  if (hay.includes('warm') || hay.includes('realorai') || hay.includes('rocket')) return 'warm-up';
  return 'activity';
}

function inferPriority(missionType: StageMissionType, activity: any): MissionPriority {
  if (missionType === 'capture') return 'core';
  const p = asString(activity?.priority).toLowerCase();
  if (p === 'core' || p === 'flex') return p;
  if (missionType === 'welcome' || missionType === 'activity') return 'core';
  return 'flex';
}

function parseScript(activity: any): ScriptStep[] {
  if (!Array.isArray(activity?.script)) return [];
  const steps: ScriptStep[] = [];
  for (const raw of activity.script) {
    if (!raw || typeof raw !== 'object') continue;
    const kind = raw.kind === 'say' ? 'say' : raw.kind === 'do' ? 'do' : null;
    const text = asString(raw.text);
    if (!kind || !text) continue;
    steps.push({ kind, text });
  }
  return steps;
}

/**
 * Resolve one mission for Stage / Briefing / Teacher's Notes.
 */
export function resolveStageMission(
  mission: { id: string; text: string; gameId?: string },
  activity: any | undefined | null
): ResolvedStageMission {
  const title =
    asString(activity?.title) ||
    mission.text.replace(/\s*\([^)]*?\d+\s*min[^)]*\)\s*/gi, ' ').trim() ||
    mission.id;

  const missionType = inferMissionType(mission.id, mission.text, activity);
  const priority = inferPriority(missionType, activity);
  const plannedMinutes =
    (typeof activity?.plannedMinutes === 'number' ? activity.plannedMinutes : null) ||
    parseDurationMinutes(asString(activity?.duration)) ||
    parseDurationMinutes(mission.text);

  const explicitPrompt = asString(activity?.prompt) || asString(activity?.projectablePrompt);
  // Fallback rule 1: no prompt → mission title huge
  const prompt = explicitPrompt || title;

  const subtitle = asString(activity?.subtitle) || undefined;
  const slideImage =
    asString(activity?.slideImage) ||
    asString(activity?.exampleImage) ||
    (typeof activity?.image === 'string' ? activity.image : '') ||
    undefined;

  const worksheet: WorksheetSpec | undefined = activity?.worksheet?.title
    ? {
        title: asString(activity.worksheet.title),
        printHtml:
          asString(activity.worksheet.printHtml) ||
          `<h1>${asString(activity.worksheet.title)}</h1><p>Copy this onto blank paper.</p>`,
      }
    : undefined;

  const script = parseScript(activity);

  // Fallback rule 2: no steps → notes blob as-is
  let notesBlob: string | undefined;
  if (script.length === 0) {
    const parts = [
      asString(activity?.teachingNotes),
      asString(activity?.teacherNotes),
      asString(activity?.description),
      flattenInstructions(activity?.instructions),
    ].filter(Boolean);
    notesBlob = parts.length ? parts.join('\n\n') : undefined;
  }

  const link = asString(activity?.link) || undefined;
  const gameId = asString(activity?.gameId) || asString(mission.gameId) || undefined;
  const interactive =
    link || gameId
      ? { kind: 'game' as const, link, gameId }
      : undefined;

  return {
    id: mission.id,
    title,
    missionType,
    priority,
    plannedMinutes,
    prompt,
    subtitle,
    slideImage: slideImage || undefined,
    worksheet,
    script,
    notesBlob,
    attachments: Array.isArray(activity?.attachments)
      ? activity.attachments.filter((attachment: any) => attachment?.url && attachment?.title)
      : [],
    interactive,
  };
}

export function resolveLevelBriefing(
  level: { id: string; title: string; teacherNotes?: string; briefSummary?: string; videoUrl?: string; slideDeckUrl?: string; iCanStatement?: string; additionalNotes?: string; worksheet?: any; worksheetsByAgeGroup?: Partial<Record<'k-2' | '3-5' | '6-8' | 'k-8', any>>; resources?: any[]; missions?: any[] },
  getActivity: (id: string) => any
): LevelBriefing {
  const missions = (level.missions || []).map((m: any) => {
    const resolved = resolveStageMission(
      { id: m.id, text: m.text || '', gameId: m.gameId },
      getActivity(m.id)
    );
    return {
      id: resolved.id,
      title: resolved.title,
      plannedMinutes: resolved.plannedMinutes,
      priority: resolved.priority,
    };
  });

  const first = (level.missions || [])[0];
  const firstResolved = first
    ? resolveStageMission(
        { id: first.id, text: first.text || '', gameId: first.gameId },
        getActivity(first.id)
      )
    : null;
  const openingSay =
    firstResolved?.script.find((s) => s.kind === 'say')?.text ||
    asString(getActivity(first?.id)?.openingSay) ||
    undefined;

  const worksheetMission = (level.missions || [])
    .map((m: any) => resolveStageMission({ id: m.id, text: m.text || '' }, getActivity(m.id)))
    .find((r) => r.worksheet);

  const summary =
    asString(level.briefSummary) ||
    htmlToText(asString(level.teacherNotes)).split('\n')[0] ||
    `Today: ${stripMissionPrefix(level.title)}.`;

  const videoUrl = asString((level as any).video?.url) || asString(level.videoUrl) || undefined;
  const worksheet = level.worksheet?.url ? level.worksheet : undefined;
  const resources = [
    ...(Array.isArray(level.resources) ? level.resources : []),
  ].filter((resource) => resource && asString(resource.url));
  const worksheetGroups = ['k-2', '3-5', '6-8', 'k-8'] as const;
  const uploadedWorksheets = worksheetGroups.flatMap((ageGroup) => {
    const resource = level.worksheetsByAgeGroup?.[ageGroup] || (ageGroup === 'k-8' ? worksheet : undefined);
    if (!resource || !asString(resource.url)) return [];
    return [{
      id: asString(resource.id) || `${level.id}-${ageGroup}-worksheet`,
      title: asString(resource.title) || `${ageGroup.toUpperCase()} worksheet`,
      url: asString(resource.url),
      ageGroup,
    }];
  });

  return {
    summary,
    teacherNotes: asString(level.teacherNotes) || undefined,
    openingSay,
    videoUrl,
    slideDeckUrl: asString(level.slideDeckUrl) || undefined,
    iCanStatement: asString(level.iCanStatement) || undefined,
    additionalNotes: asString(level.additionalNotes) || undefined,
    resources,
    uploadedWorksheets,
    worksheet: worksheetMission?.worksheet,
    missions,
  };
}

export function lookAheadMessage(
  missions: ResolvedStageMission[],
  currentIndex: number
): string | null {
  const next = missions[currentIndex + 1];
  if (!next) return null;
  if (next.worksheet) {
    return `Next mission uses the ${next.worksheet.title} sheet — projecting it`;
  }
  if (next.missionType === 'capture') {
    return 'Up next: photograph team drawings — phone ready';
  }
  return null;
}
